import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Shield, Plus, Search, AlertCircle, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL } from '@/lib/backend';
export default function SSLCertificatesModule() {
  const [certificates, setCertificates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: '', domain: '', issuer: '', expiry_date: '', notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [certRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/ssl-certificates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (certRes.ok) setCertificates(await certRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/ssl-certificates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowAddDialog(false);
        loadData();
      }
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleDelete = async (id, domain) => {
    if (!confirm(`Delete SSL certificate for ${domain}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/ssl-certificates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = certificates.filter(c =>
    c.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">SSL Certificates</h1>
          <p className="text-sm text-slate-500 mt-1">SSL/TLS certificate tracking</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />Add Certificate
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add SSL Certificate</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Domain *</Label><Input value={formData.domain} onChange={(e) => setFormData({...formData, domain: e.target.value})} placeholder="*.example.com" required /></div>
                <div><Label>Issuer</Label><Input value={formData.issuer} onChange={(e) => setFormData({...formData, issuer: e.target.value})} placeholder="Let's Encrypt" /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={formData.expiry_date} onChange={(e) => setFormData({...formData, expiry_date: e.target.value})} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Add Certificate</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search certificates..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No certificates found</p></CardContent></Card> : (
        <div className="space-y-3">
          {filtered.map((cert) => (
            <Card key={cert.id} className={`hover:shadow-md transition-shadow ${cert.is_expiring_soon ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-teal-500'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="w-5 h-5 text-teal-600" />
                      <h3 className="font-semibold text-slate-800">{cert.domain}</h3>
                      {cert.is_expiring_soon && <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" />Expiring Soon</Badge>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div><p className="text-slate-500">Organization</p><p className="font-medium">{cert.organization_name}</p></div>
                      {cert.issuer && <div><p className="text-slate-500">Issuer</p><p className="font-medium">{cert.issuer}</p></div>}
                      {cert.expiry_date && <div><p className="text-slate-500">Expires</p><p className="font-medium">{new Date(cert.expiry_date).toLocaleDateString()}</p></div>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(cert.id, cert.domain)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
